import React from 'react'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Wand2, Image, Share2, FileText, Eye, Download, Crown } from 'lucide-react'
import { AI_ASSETS } from './brandDashboardData'
import { AIAssetGenerator } from '../ai/AIAssetGenerator'

interface AIAssetsTabProps {
  brand: any
  onGenerateAssets: () => void
}

export function AIAssetsTab({ brand, onGenerateAssets }: AIAssetsTabProps) {
  return (
    <div className="space-y-8">
      {/* AI Asset Generator */}
      <AIAssetGenerator 
        brandData={{
          name: brand.name,
          industry: brand.metadata?.industry,
          colors: brand.colors || ['#2563EB', '#1E40AF'],
          fonts: brand.fonts || ['Inter'],
          tone: brand.tone || 'Professional',
          values: brand.values || []
        }}
        onAssetGenerated={(asset) => {
          console.log('New asset generated:', asset)
          // TODO: Add to assets list
        }}
      />

      {/* Existing Assets */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Your Generated Assets</h3>
            <p className="text-slate-600">Previously created AI-powered marketing materials</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {AI_ASSETS.map((asset) => (
            <Card key={asset.id} className="overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <div className="text-slate-400">
                  {asset.type === 'logo' && <Image className="w-12 h-12" />}
                  {asset.type === 'social' && <Share2 className="w-12 h-12" />}
                  {asset.type === 'marketing' && <FileText className="w-12 h-12" />}
                </div>
              </div>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{asset.title}</CardTitle>
                  {asset.isPro && <Crown className="w-4 h-4 text-blue-500" />}
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="text-xs capitalize">
                    {asset.type}
                  </Badge>
                  {asset.isWatermarked && (
                    <Badge variant="secondary" className="text-xs">
                      Watermarked
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" className="flex-1">
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                  <Button size="sm" className="flex-1">
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {AI_ASSETS.length === 0 && (
          <Card className="p-12 text-center">
            <Wand2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No AI assets yet</h3>
            <p className="text-slate-600 mb-6">
              Generate your first AI-powered marketing materials to get started
            </p>
            <Button 
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              onClick={onGenerateAssets}
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Generate Assets
            </Button>
          </Card>
        )}
      </div>
    </div>
  )
}